import { createTheme } from '@mui/material/styles';
import tokens, { generateColor, generateOpacityColor } from '@verifime/design-tokens';
import {
  commonComponentsOverride,
  paletteGrey,
  semanticBorderRadius,
  semanticFontWeight,
  semanticFontSize,
  semanticLetterSpacing,
  semanticLineHeight,
  semanticSpacing,
  semanticSize,
  typographyTheme,
} from './common';

const errorContent = generateColor(tokens.brandColorError, '100');
const errorFill = generateOpacityColor(generateColor(tokens.brandColorError, '500'), 0.3);
const warningContent = generateColor(tokens.brandColorWarning, '100');
const warningFill = generateOpacityColor(generateColor(tokens.brandColorWarning, '500'), 0.3);
const infoContent = generateColor(tokens.brandColorInfo, '100');
const infoFill = generateOpacityColor(generateColor(tokens.brandColorInfo, '500'), 0.2);
const successContent = generateColor(tokens.brandColorSuccess, '100');
const successFill = generateOpacityColor(generateColor(tokens.brandColorSuccess, '500'), 0.2);

const theme = createTheme({
  semantic: {
    borderRadius: semanticBorderRadius,
    fontWeight: semanticFontWeight,
    fontSize: semanticFontSize,
    letterSpacing: semanticLetterSpacing,
    lineHeight: semanticLineHeight,
    spacing: semanticSpacing,
    size: semanticSize,
  },
  logo: {
    width: semanticSize['2xs'],
    padding: semanticSpacing['2xs'],
    borderRadius: semanticSpacing['2xs'],
  },
  card: {
    padding: semanticSpacing.base,
    borderRadius: semanticBorderRadius.sm,
    borderWidth: semanticSpacing['4xs'],
    contentGap: semanticSpacing.sm,
    itemGap: semanticSpacing.sm,
  },
  palette: {
    mode: 'dark',
    primary: {
      main: generateColor(tokens.brandColorPrimary, '400'),
      dark: generateColor(tokens.brandColorPrimary, '300'),
      light: generateColor(tokens.brandColorPrimary, '600'),
      contrastText: tokens.colorWhite,
    },
    secondary: {
      main: generateColor(tokens.brandColorSecondary, '400'),
      dark: generateColor(tokens.brandColorSecondary, '300'),
      light: generateColor(tokens.brandColorSecondary, '600'),
      contrastText: tokens.colorWhite,
    },
    error: {
      main: generateColor(tokens.brandColorError, '500'),
      dark: generateColor(tokens.brandColorError, '400'),
      light: generateColor(tokens.brandColorError, '600'),
      contrastText: tokens.colorWhite,
    },
    warning: {
      main: generateColor(tokens.brandColorWarning, '500'),
      dark: generateColor(tokens.brandColorWarning, '400'),
      light: generateColor(tokens.brandColorWarning, '600'),
      contrastText: generateOpacityColor(tokens.colorBlack, 0.5),
    },
    info: {
      main: generateColor(tokens.brandColorInfo, '500'),
      dark: generateColor(tokens.brandColorInfo, '400'),
      light: generateColor(tokens.brandColorInfo, '600'),
      contrastText: tokens.colorWhite,
    },
    success: {
      main: generateColor(tokens.brandColorSuccess, '500'),
      dark: generateColor(tokens.brandColorSuccess, '400'),
      light: generateColor(tokens.brandColorSuccess, '600'),
      contrastText: tokens.colorWhite,
    },
    text: {
      primary: generateOpacityColor(tokens.colorWhite, 0.9),
      secondary: generateOpacityColor(tokens.colorWhite, 0.6),
      disabled: generateOpacityColor(tokens.colorWhite, 0.4),
      // Three steps lighter than the light theme's, because the 500s sit at roughly 2.9 and 2.4
      // against dark paper. See the note on TypeText.success in packages/components/src/index.ts.
      success: generateColor(tokens.brandColorSuccess, '300'),
      danger: generateColor(tokens.brandColorError, '300'),
    },
    background: {
      default: tokens.colorGrey900,
      paper: tokens.colorGrey800,
    },
    action: {
      disabled: generateOpacityColor(tokens.colorWhite, 0.4),
      disabledBackground: generateOpacityColor(tokens.colorWhite, 0.1),
      disabledOpacity: 0.4,
      focus: generateOpacityColor(tokens.colorWhite, 0.1),
      focusOpacity: 0.1,
      selected: generateColor(tokens.brandColorPrimary, '400'),
      selectedOpacity: 0.4,
      hover: generateOpacityColor(tokens.colorWhite, 0.05),
      hoverOpacity: 0.05,
      active: generateOpacityColor(tokens.colorWhite, 0.5),
      activatedOpacity: 0.1,
    },
    surface: {
      logo: tokens.colorWhite,
      brandSubtle: generateColor(tokens.brandColorPrimary, '900'),
    },
    border: {
      decorative: tokens.colorGrey400,
    },
    status: {
      success: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorSuccess, '500'),
        icon: tokens.colorWhite,
      },
      warning: {
        text: generateOpacityColor(tokens.colorBlack, 0.5),
        fill: generateColor(tokens.brandColorWarning, '400'),
        icon: generateOpacityColor(tokens.colorBlack, 0.5),
      },
      error: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorError, '500'),
        icon: tokens.colorWhite,
      },
      info: {
        text: generateColor(tokens.brandColorInfo, '400'),
        border: generateColor(tokens.brandColorInfo, '400'),
      },
    },
    interactive: {
      disabled: {
        text: tokens.colorWhite,
        fill: generateOpacityColor(tokens.colorWhite, 0.4),
        icon: tokens.colorWhite,
      },
      enabled: {
        text: generateOpacityColor(tokens.colorWhite, 0.5),
        fill: generateOpacityColor(tokens.colorWhite, 0.5),
        icon: generateOpacityColor(tokens.colorWhite, 0.25),
      },
    },
    risk: {
      extreme: {
        text: tokens.colorWhite,
        fill: tokens.colorGrey800,
        border: generateOpacityColor(tokens.colorWhite, 0.9),
        icon: tokens.colorWhite,
      },
      high: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorError, '500'),
        icon: tokens.colorWhite,
      },
      medium: {
        text: generateOpacityColor(tokens.colorBlack, 0.5),
        fill: generateColor(tokens.brandColorWarning, '400'),
        icon: generateOpacityColor(tokens.colorBlack, 0.5),
      },
      low: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorSuccess, '500'),
        icon: tokens.colorWhite,
      },
      pending: {
        text: generateOpacityColor(tokens.colorWhite, 0.4),
        fill: tokens.colorGrey800,
        border: generateOpacityColor(tokens.colorWhite, 0.4),
        icon: generateOpacityColor(tokens.colorWhite, 0.5),
      },
    },
    confidence: {
      none: {
        text: generateOpacityColor(tokens.colorWhite, 0.6),
        fill: tokens.colorGrey800,
      },
      pending: {
        text: generateOpacityColor(tokens.colorWhite, 0.6),
        fill: tokens.colorGrey600,
      },
      low: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorError, '500'),
      },
      substantial: {
        text: generateOpacityColor(tokens.colorBlack, 0.5),
        fill: generateColor(tokens.brandColorWarning, '400'),
      },
      high: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorSuccess, '500'),
      },
      /**
       * The "what do you do now" box beneath a card's statement. The light theme pairs `'050'`
       * fill with `'800'` text; here that pair is inverted to `'900'` fill with `'100'` text, so
       * the pale tint and dark text of the light theme become a dark tint and pale text against
       * dark paper. That is the same direction `confidence.none` takes with its greys just above
       * (`colorGrey100` in light, `colorGrey800` in dark).
       */
      guidance: {
        fill: generateColor(tokens.brandColorPrimary, '900'),
        text: generateColor(tokens.brandColorPrimary, '100'),
      },
    },
    // Identical across all four themes on purpose: these sit over a video frame, not over
    // the page surface, so they take their contrast from the capture rather than the theme.
    mediaOverlay: {
      scrim: generateOpacityColor(tokens.colorBlack, 0.72),
      control: generateOpacityColor(tokens.colorWhite, 0.22),
      controlHover: generateOpacityColor(tokens.colorWhite, 0.34),
      track: generateOpacityColor(tokens.colorWhite, 0.28),
      halo: generateOpacityColor(tokens.colorWhite, 0.34),
    },
    entity: {
      verified: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorSuccess, '500'),
        icon: tokens.colorWhite,
      },
      rejected: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorError, '500'),
        icon: tokens.colorWhite,
      },
      draft: {
        text: tokens.colorWhite,
        fill: generateOpacityColor(tokens.colorWhite, 0.4),
        icon: generateOpacityColor(tokens.colorWhite, 0.5),
      },
      inProgress: {
        text: tokens.colorWhite,
        fill: generateOpacityColor(tokens.colorWhite, 0.4),
        icon: tokens.colorWhite,
      },
      empty: {
        text: tokens.colorWhite,
        fill: generateOpacityColor(tokens.colorWhite, 0.4),
        icon: tokens.colorWhite,
      },
    },
    grey: paletteGrey,
  },
  typography: typographyTheme,
  components: {
    MuiButton: {
      variants: [
        ...commonComponentsOverride.MuiButton!.variants!,
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            color: generateColor(tokens.brandColorPrimary, '300'),
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            color: generateColor(tokens.brandColorPrimary, '300'),
          },
        },
        {
          props: { color: 'error' },
          style: {
            color: generateColor(tokens.brandColorError, '300'),
          },
        },
      ],
    },
    MuiLink: {
      defaultProps: {
        color: generateColor(tokens.brandColorPrimary, '300'),
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: errorFill,
          '& .MuiAlertTitle-root': {
            color: errorContent,
          },
          '& .MuiTypography-root': {
            color: errorContent,
          },
          '& button': {
            color: errorContent,
          },
        },
        standardWarning: {
          backgroundColor: warningFill,
          '& .MuiAlertTitle-root': {
            color: warningContent, // yellow
          },
          '& .MuiTypography-root': {
            color: warningContent, // black
          },
          '& button': {
            color: warningContent,
          },
        },
        standardInfo: {
          backgroundColor: infoFill,
          '& .MuiAlertTitle-root': {
            color: infoContent,
          },
          '& .MuiTypography-root': {
            color: infoContent,
          },
          '& button': {
            color: infoContent,
          },
        },
        standardSuccess: {
          backgroundColor: successFill,
          '& .MuiAlertTitle-root': {
            color: successContent,
          },
          '& .MuiTypography-root': {
            color: successContent,
          },
          '& button': {
            color: successContent,
          },
        },
      },
    },
  },
});

export default theme;
